import { Controller, FieldValues, useForm } from 'react-hook-form'
import { SelectItem, getSelectItemOptionLabel, normalizeSelectItems, isSelectItemEqual } from '../../../types/SelectItem'
import { useCallback, useContext, useEffect, useState } from 'react'
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Grid,
  TextField,
  Tooltip,
  createFilterOptions,
} from '@mui/material'
import {
  useCustomerAutocompleteOptionsLazyQuery,
  useCustomerTagsOptionsQuery,
} from '@kistill/client/data-access'
import { FilterControls } from './FilterControls'
import { ListViewContext } from '../../../contexts/ListViewContext'
import {
  handleOnSubmit,
  OnFilterType,
} from '../../../hooks/useHandleOnSubmitFilters'
import { getHiddenFilterFields } from '../filterConfig'
import { useClearFiltersOnColumnHide } from '../../../hooks/useClearFiltersOnColumnHide'
import { useFilterSync } from '../../../hooks/useFilterSync'

interface CustomerFilterProps {
  onFilter: OnFilterType
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  columnVisibility?: { [key: string]: boolean }
  initialFilterParams?: Record<string, unknown>
}

export function CustomerFilters({
  onFilter,
  selected,
  setSelected,
  columnVisibility = {},
  initialFilterParams,
}: CustomerFilterProps) {
  // Get hidden filter fields based on column visibility
  const hiddenFields = getHiddenFilterFields('customer', columnVisibility)
  const { handleSubmit, watch, control, register, setValue, reset } = useForm({ shouldUnregister: true })
  const { showOnlySelected, updateShowOnlySelected } =
    useContext(ListViewContext)
  const [inputValueCustomer, setInputValueCustomer] = useState('')
  const [customerOptions, setCustomerOptions] = useState<SelectItem[]>([])

  const { data: tagsData } = useCustomerTagsOptionsQuery()
  const tagOptions = tagsData?.customerTagsOptions as SelectItem[]

  const [getCustomerOptions] = useCustomerAutocompleteOptionsLazyQuery({
    variables: { query: inputValueCustomer },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.customerOptions) {
        setCustomerOptions(
          data.customerOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const onSubmit = useCallback(
    (data: FieldValues) => {
      handleOnSubmit(onFilter)(data)
    },
    [onFilter],
  )

  const { formStateRef, clearFilters, clearSpecificFields } = useFilterSync({
    initialFilterParams,
    reset,
    watch,
    onSubmit,
    getSyncKey: (normalized) =>
      normalized === null ? null : JSON.stringify(normalized),
    getClearSyncKey: () => 'customer',
    onEmptySync: () => {
      setInputValueCustomer('')
    },
    onClear: () => {
      setInputValueCustomer('')
    },
  })

  const handleInputChangeCustomer = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueCustomer(value)
  }

  const filterOptions = createFilterOptions<SelectItem>({
    trim: true,
  })

  const deselectCheckboxChange = () => {
    setSelected(new Set())
    if (showOnlySelected) {
      setValue('selectedGuids', null)
      updateShowOnlySelected(!showOnlySelected)
      reset({})
      setInputValueCustomer('')
    }
  }

  useEffect(() => {
    getCustomerOptions() // Trigger the query to fetch customer options
  }, [getCustomerOptions])

  // Clear filter values when their corresponding columns are hidden
  useClearFiltersOnColumnHide(
    'customer',
    columnVisibility,
    setValue,
    clearSpecificFields,
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        {!hiddenFields.has('tengilidirSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='tengilidirSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(customerOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, tengilidirSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  onInputChange={handleInputChangeCustomer}
                  inputValue={inputValueCustomer}
                  value={value || []}
                  clearOnBlur={false}
                  filterOptions={filterOptions}
                  renderOption={(props, option) => (
                    <li {...props} key={(option as SelectItem).id}>
                      {getSelectItemOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label='Nafn aðila' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('skyring') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='skyring'
              render={({ field }) => (
                <Tooltip title='Leitaðu af mörgum orðum með því að setja , á milli orðanna.'>
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label='Skýring'
                    fullWidth
                    size='small'
                  />
                </Tooltip>
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('tagSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='tagSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(tagOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, tagSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  value={value || []}
                  clearOnBlur={false}
                  renderOption={(props, option) => (
                    <li {...props} key={(option as SelectItem).id}>
                      {getSelectItemOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => <TextField {...params} label='Tög' />}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('athugasemd') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='athugasemd'
              render={({ field }) => (
                <Tooltip title='Leitaðu af mörgum orðum með því að setja , á milli orðanna.'>
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label='Athugasemd'
                    fullWidth
                    size='small'
                  />
                </Tooltip>
              )}
            />
          </Grid>
        )}
      </Grid>
      <FilterControls
        selected={selected}
        clearFilters={clearFilters}
        register={register}
        deselectCheckboxChange={deselectCheckboxChange}
      />
    </form>
  )
}
